import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Avatar,
  CircularProgress, Alert
} from '@mui/material';
import { Visibility, VisibilityOff, Search, Download, Refresh, Work, LocationOn, AttachMoney } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/rojgar';

const JobModeration = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, paused: 0, expired: 0 });

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/stats`);
      if (res.data.success) {
        setStats({
          total: res.data.data.totalJobs || 0,
          active: res.data.data.activeJobs || 0,
          paused: 0,
          expired: 0
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      
      const res = await axios.get(`${API_BASE}/admin/jobs`, { params });
      if (res.data.success) {
        setJobs(res.data.data);
        setTotal(res.data.pagination?.total || res.data.data.length);
      }
    } catch (err) {
      setError('Error fetching jobs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchJobs();
  }, [page, rowsPerPage, statusFilter]);

  const toggleJobVisibility = async (jobId) => {
    try {
      await axios.put(`${API_BASE}/admin/job/${jobId}/visibility`);
      fetchJobs();
    } catch (err) {
      setError('Error toggling job visibility: ' + err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'expired': return 'error';
      case 'closed': return 'default';
      default: return 'primary';
    }
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `₹${min.toLocaleString()} - ₹${max.toLocaleString()}`;
    if (min) return `₹${min.toLocaleString()}+`;
    return `Up to ₹${max.toLocaleString()}`;
  };

  const filteredJobs = jobs.filter(job => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      job.title?.toLowerCase().includes(searchLower) ||
      job.providerId?.companyName?.toLowerCase().includes(searchLower) ||
      job.city?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
          Rojgar Job Moderation
        </Typography>
        <IconButton onClick={() => { fetchStats(); fetchJobs(); }} color="primary">
          <Refresh />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total Jobs', value: stats.total, color: '#2196F3', icon: <Work /> },
          { label: 'Active Jobs', value: stats.active, color: '#4CAF50', icon: <Work /> },
          { label: 'Paused Jobs', value: stats.paused, color: '#FF9800', icon: <Work /> },
          { label: 'Expired Jobs', value: stats.expired, color: '#F44336', icon: <Work /> }
        ].map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ bgcolor: stat.color, color: 'white' }}>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                </Box>
                {stat.icon}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by job title, company or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Job Status</InputLabel>
              <Select value={statusFilter} label="Job Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" startIcon={<Download />} fullWidth>Export</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead sx={{ bgcolor: '#FF5722' }}>
                <TableRow>
                  {['Job Title', 'Company', 'Location', 'Salary', 'Applications', 'Status', 'Posted', 'Actions'].map(header => (
                    <TableCell key={header} sx={{ color: 'white', fontWeight: 'bold' }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredJobs.map((job) => (
                  <TableRow key={job._id} hover sx={{ opacity: job.isHidden ? 0.5 : 1 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Work sx={{ color: '#FF5722' }} />
                        <Box>
                          <Typography fontWeight="bold">{job.title}</Typography>
                          <Typography variant="caption" color="textSecondary">{job.jobType} • {job.workMode}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 30, height: 30, bgcolor: '#FF5722' }} src={job.providerId?.companyLogo}>
                          {(job.providerId?.companyName || 'C')[0]}
                        </Avatar>
                        {job.providerId?.companyName || 'N/A'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOn sx={{ fontSize: 14, mr: 0.5, color: 'gray' }} />
                        {job.city}, {job.state}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoney sx={{ fontSize: 14, color: 'green' }} />
                        {formatSalary(job.salaryMin, job.salaryMax)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={job.applicationCount || 0} color="primary" size="small" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip label={job.status} color={getStatusColor(job.status)} size="small" />
                        {job.isHidden && <Chip label="Hidden" size="small" color="error" />}
                      </Box>
                    </TableCell>
                    <TableCell>{job.createdAt ? new Date(job.createdAt).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedJob(job); setDetailDialog(true); }}>
                        <Visibility />
                      </IconButton>
                      <IconButton 
                        color={job.isHidden ? 'error' : 'default'} 
                        onClick={() => toggleJobVisibility(job._id)}
                        title={job.isHidden ? 'Show Job' : 'Hide Job'}
                      >
                        {job.isHidden ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredJobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No jobs found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#FF5722', color: 'white' }}>
          Job Details - {selectedJob?.title}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedJob && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{selectedJob.title}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip label={selectedJob.status} color={getStatusColor(selectedJob.status)} />
                  <Chip label={selectedJob.jobType} variant="outlined" />
                  <Chip label={selectedJob.workMode} variant="outlined" />
                </Box>
              </Grid>
              <Grid item xs={6}><Typography><b>Company:</b> {selectedJob.providerId?.companyName || 'N/A'}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Location:</b> {selectedJob.city}, {selectedJob.state}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Salary:</b> {formatSalary(selectedJob.salaryMin, selectedJob.salaryMax)}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Experience:</b> {selectedJob.experienceMin || 0}-{selectedJob.experienceMax || 0} years</Typography></Grid>
              <Grid item xs={6}><Typography><b>Openings:</b> {selectedJob.numberOfOpenings || 1}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Applications:</b> {selectedJob.applicationCount || 0}</Typography></Grid>
              <Grid item xs={12}>
                <Typography><b>Description:</b></Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{selectedJob.description || 'No description'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><b>Requirements:</b></Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{selectedJob.requirements || 'No requirements specified'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><b>Skills:</b></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {(selectedJob.skills || []).map((skill, i) => (
                    <Chip key={i} label={skill} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={6}><Typography><b>Created:</b> {selectedJob.createdAt ? new Date(selectedJob.createdAt).toLocaleString() : 'N/A'}</Typography></Grid>
              <Grid item xs={6}><Typography><b>Expires:</b> {selectedJob.expiryDate ? new Date(selectedJob.expiryDate).toLocaleDateString() : 'N/A'}</Typography></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
          {selectedJob && (
            <Button 
              variant="contained" 
              color={selectedJob.isHidden ? 'success' : 'error'}
              onClick={() => { toggleJobVisibility(selectedJob._id); setDetailDialog(false); }}
            >
              {selectedJob.isHidden ? 'Show Job' : 'Hide Job'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobModeration;
